import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Alert, Box, Button, Card, Chip, CircularProgress,
    Divider, Grid, IconButton, List,
    Stack, Tooltip, Typography,
} from "@mui/material";
import {
    Timeline, TimelineConnector, TimelineContent,
    TimelineDot, TimelineItem, TimelineSeparator,
} from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PersonIcon from "@mui/icons-material/Person";
import SwapCallsIcon from "@mui/icons-material/SwapCalls";
import CommentIcon from "@mui/icons-material/Comment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import AuditStatusChip from "../components/AuditStatusChip";
import AuditPrioridadChip from "../components/AuditPrioridadChip";
import AuditTrackingPanel from "../components/AuditTrackingPanel";
import AuditReasignarModal from "../components/AuditReasignarModal";
import AuditAdjuntoItem from "../components/AuditAdjuntoItem";
import AuditEditModal from "../components/AuditEditModal";
import { useAuditUser } from "../hooks/useAuditUser";
import { getItem, getEstados } from "../services/audit.service";

// ── Helpers ───────────────────────────────────────────────────
const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
    });
};
const fmtDt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("es-AR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};
const fmtSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const isVencido = (f) => f && new Date(f) < new Date();

// ── Config timeline por tipo acción ──────────────────────────
const ACCION = {
    CREACION: { icon: <AddCircleOutlineIcon />, color: "success", label: "Ítem creado" },
    ASIGNACION: { icon: <PersonIcon />, color: "info", label: "Asignación" },
    REASIGNACION: { icon: <SwapCallsIcon />, color: "warning", label: "Reasignación" },
    CAMBIO_ESTADO: { icon: <FiberManualRecordIcon />, color: "primary", label: "Cambio de estado" },
    COMENTARIO: { icon: <CommentIcon />, color: "grey", label: "Comentario" },
    ADJUNTO: { icon: <AttachFileIcon />, color: "secondary", label: "Adjunto" },
    CAMBIO_FECHA: { icon: <EventIcon />, color: "warning", label: "Cambio de fecha" },
    EDICION: { icon: <EditIcon />, color: "error", label: "Edición" },
};

// ── Info row compacto ─────────────────────────────────────────
function InfoRow({ icon, label, value, valueColor }) {
    return (
        <Stack direction="row" alignItems="flex-start" gap={1} sx={{ py: 0.6 }}>
            <Box sx={{ color: "text.disabled", display: "flex", pt: "1px", flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { fontSize: 14 } })}
            </Box>
            <Box flex={1}>
                <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.2}>
                    {label}
                </Typography>
                {typeof value === "string" ? (
                    <Typography variant="body2" color={valueColor || "text.primary"} lineHeight={1.4}>
                        {value}
                    </Typography>
                ) : value}
            </Box>
        </Stack>
    );
}

// ── Texto acción del tracking ─────────────────────────────────
function AccionLabel({ t }) {
    if (t.tipo_accion === "CREACION")
        return <Typography variant="caption" color="text.secondary">Ítem creado</Typography>;
    if (t.tipo_accion === "ASIGNACION")
        return <Typography variant="caption" color="text.secondary">Asignado a <strong>{t.controlador_nuevo}</strong></Typography>;
    if (t.tipo_accion === "REASIGNACION")
        return (
            <Typography variant="caption" color="text.secondary">
                Reasignado: <strong>{t.controlador_anterior}</strong> → <strong>{t.controlador_nuevo}</strong>
            </Typography>
        );
    if (t.tipo_accion === "CAMBIO_ESTADO")
        return (
            <Typography variant="caption" color="text.secondary">
                Estado: {t.status_anterior_desc && <><strong>{t.status_anterior_desc}</strong> → </>}
                <strong style={{ color: t.status_nuevo_color }}>{t.status_nuevo_desc}</strong>
            </Typography>
        );
    if (t.tipo_accion === "EDICION")
        return <Typography variant="caption" color="error.main" fontWeight={600}>✏️ Ítem editado</Typography>;
    return null;
}

// ══════════════════════════════════════════════════════════════
export default function AuditDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { username, puedeVerTodo, puedeReasignar } = useAuditUser();

    const [item, setItem] = useState(null);
    const [estados, setEstados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openTracking, setOpenTracking] = useState(false);
    const [openReasignar, setOpenReasignar] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [itemData, estadosData] = await Promise.all([
                getItem(id, username),
                getEstados(),
            ]);
            setItem(itemData);
            setEstados(estadosData);
        } catch (e) {
            setError(
                e?.response?.status === 403 ? "No tenés acceso a este ítem." :
                    e?.response?.status === 404 ? "El ítem no existe." :
                        "Error al cargar el ítem."
            );
        } finally {
            setLoading(false);
        }
    }, [id, username]);

    useEffect(() => { cargar(); }, [cargar]);

    if (loading) return (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress size={32} />
        </Box>
    );

    if (error) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={() => navigate(-1)}>Volver</Button>
            }>{error}</Alert>
        </Box>
    );

    if (!item) return null;

    const statusActual = {
        id: item.status_id, codigo: item.status_codigo,
        descripcion: item.status_desc, color: item.status_color,
    };
    const isCerrado = ["CERRADO", "RESUELTO"].includes(item.status_codigo);
    const vencido = isVencido(item.fecha_requerida) && !isCerrado;


    return (
        <Box sx={{ backgroundColor: "background.default", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

            {/* ── Header sticky ── */}
            <Box sx={{
                position: "sticky", top: 0, zIndex: 10,
                backgroundColor: "background.default",
                px: { xs: 1.5, md: 2.5 }, pt: { xs: 1.5, md: 2 }, pb: 1.5,
                borderBottom: "1px solid", borderColor: "divider",
            }}>
                <Stack direction="row" alignItems="flex-start" gap={1}>
                    <IconButton onClick={() => navigate(-1)} size="small" sx={{ mt: 0.2, flexShrink: 0 }}>
                        <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row"
                            justifyContent="space-between" alignItems="flex-start"
                            gap={1} flexWrap="wrap">
                            <Box minWidth={0} flex={1}>
                                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" mb={0.5}>
                                    <Typography variant="caption" color="text.disabled" fontWeight={700}>
                                        #{item.id}
                                    </Typography>
                                    <AuditStatusChip
                                        codigo={item.status_codigo}
                                        descripcion={item.status_desc}
                                        color={item.status_color}
                                    />
                                    <AuditPrioridadChip prioridad={item.prioridad} />
                                    {vencido && (
                                        <Chip
                                            icon={<WarningAmberIcon sx={{ fontSize: "0.85rem !important" }} />}
                                            label="Vencido" size="small" color="warning" variant="outlined"
                                        />
                                    )}
                                </Stack>
                                <Typography variant="h5" noWrap>{item.titulo}</Typography>
                            </Box>

                            <Stack direction="row" gap={1} flexShrink={0} flexWrap="wrap">
                                {puedeVerTodo && !isCerrado && (
                                    <Button variant="outlined" startIcon={<EditIcon />}
                                        onClick={() => setOpenEdit(true)}>
                                        Editar
                                    </Button>
                                )}
                                {puedeReasignar && !isCerrado && (
                                    <Button variant="outlined" startIcon={<SwapHorizIcon />}
                                        onClick={() => setOpenReasignar(true)}>
                                        Reasignar
                                    </Button>
                                )}
                                {!isCerrado && (
                                    <Button variant="contained" startIcon={<AddCommentIcon />}
                                        onClick={() => setOpenTracking(true)}>
                                        Registrar avance
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            {/* ── Contenido scrolleable ── */}
            <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2, flex: 1 }}>
                <Grid container spacing={2}>

                    {/* ══ COL IZQUIERDA ══ */}
                    <Grid item xs={12} md={4}>

                        {/* Datos del ítem */}
                        <Card sx={{ mb: 2 }}>
                            <Box sx={{ p: "12px 16px" }}>
                                <Typography variant="overline" display="block" mb={1}>Datos del ítem</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <InfoRow icon={<LocationOnIcon />} label="Sucursal" value={item.sucursal_nombre} />
                                {item.categoria && (
                                    <InfoRow icon={<FiberManualRecordIcon />} label="Categoría" value={item.categoria} />
                                )}
                                <InfoRow icon={<EventIcon />} label="Detección" value={fmt(item.fecha_deteccion)} />
                                <InfoRow
                                    icon={<AccessTimeIcon />} label="Límite de solución"
                                    value={fmt(item.fecha_requerida)}
                                    valueColor={vencido ? "error.main" : undefined}
                                />
                                {item.fecha_cierre && (
                                    <InfoRow icon={<AccessTimeIcon />} label="Fecha de cierre" value={fmt(item.fecha_cierre)} />
                                )}
                                <InfoRow icon={<AccessTimeIcon />} label="Creado" value={fmtDt(item.created_at)} />
                            </Box>
                        </Card>

                        {/* Responsables */}
                        <Card sx={{ mb: 2 }}>
                            <Box sx={{ p: "12px 16px" }}>
                                <Typography variant="overline" display="block" mb={1}>Responsables</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <InfoRow icon={<PersonIcon />} label="Auditor creador"
                                    value={item.auditor_nombre || item.auditor_username} />
                                <InfoRow
                                    icon={<PersonIcon />} label="Controlador actual"
                                    value={item.controlador_nombre || item.controlador_username || "Sin asignar"}
                                    valueColor={!item.controlador_username ? "text.disabled" : undefined}
                                />
                                {item.controladores?.length > 1 && (
                                    <Box mt={1}>
                                        <Typography variant="caption" color="text.disabled" display="block" mb={0.5}>
                                            Historial de asignaciones
                                        </Typography>
                                        {item.controladores.map((c) => (
                                            <Stack key={c.id} direction="row" alignItems="center" gap={0.75} mb={0.3}>
                                                <FiberManualRecordIcon sx={{
                                                    fontSize: 7,
                                                    color: c.es_actual ? "success.main" : "text.disabled",
                                                }} />
                                                <Typography variant="caption"
                                                    color={c.es_actual ? "text.primary" : "text.disabled"}
                                                    fontWeight={c.es_actual ? 600 : 400}>
                                                    {c.controlador_nombre || c.controlador_username}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Card>

                        {/* Adjuntos del ítem */}
                        {item.adjuntos?.length > 0 && (
                            <Card>
                                <Box sx={{ p: "12px 16px" }}>
                                    <Typography variant="overline" display="block" mb={1}>
                                        Adjuntos ({item.adjuntos.length})
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />
                                    <List dense disablePadding>
                                        {item.adjuntos.map(adj => <AuditAdjuntoItem key={adj.id} adj={adj} />)}
                                    </List>
                                </Box>
                            </Card>
                        )}
                    </Grid>

                    {/* ══ COL DERECHA ══ */}
                    <Grid item xs={12} md={8}>

                        {/* Descripción */}
                        <Card sx={{ mb: 2 }}>
                            <Box sx={{ p: "12px 16px" }}>
                                <Typography variant="overline" display="block" mb={1}>Descripción / Observación</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                                    {item.descripcion}
                                </Typography>
                            </Box>
                        </Card>

                        {/* Timeline */}
                        <Card>
                            <Box sx={{ p: "12px 16px 0 16px" }}>
                                <Typography variant="overline" display="block" mb={1}>
                                    Historial de seguimiento ({item.tracking?.length || 0})
                                </Typography>
                                <Divider />
                            </Box>
                            <Box sx={{
                                overflowY: "auto",
                                maxHeight: "55vh",
                                px: 2,
                                pb: 2,
                                pt: 1,
                                overscrollBehavior: "contain",
                            }}>
                                {!item.tracking?.length ? (
                                    <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                        Sin movimientos registrados.
                                    </Typography>
                                ) : (
                                    <Timeline sx={{ p: 0, "& .MuiTimelineItem-root:before": { display: "none" } }}>
                                        {item.tracking.map((t, idx) => {
                                            const cfg = ACCION[t.tipo_accion] || ACCION.COMENTARIO;
                                            const esUlt = idx === item.tracking.length - 1;
                                            return (
                                                <TimelineItem key={t.id}>
                                                    <TimelineSeparator>
                                                        <TimelineDot color={cfg.color}>
                                                            {React.cloneElement(cfg.icon, { sx: { fontSize: 12 } })}
                                                        </TimelineDot>
                                                        {!esUlt && <TimelineConnector />}
                                                    </TimelineSeparator>
                                                    <TimelineContent sx={{ pb: esUlt ? 0 : 1.5, pt: 0 }}>
                                                        <Box sx={{
                                                            backgroundColor: "grey.50",
                                                            border: "1px solid", borderColor: "divider",
                                                            borderRadius: 1.5, p: "8px 12px",
                                                        }}>
                                                            <Stack direction="row" justifyContent="space-between"
                                                                alignItems="center" flexWrap="wrap" gap={0.5} mb={0.25}>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {t.usuario_nombre || t.username}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.disabled">
                                                                    {fmtDt(t.created_at)}
                                                                </Typography>
                                                            </Stack>
                                                            <AccionLabel t={t} />
                                                            {t.comentario && (
                                                                <Typography variant="body2"
                                                                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, mt: 0.5 }}>
                                                                    {t.comentario}
                                                                </Typography>
                                                            )}
                                                            {t.adjuntos?.length > 0 && (
                                                                <List dense disablePadding sx={{ mt: 0.75 }}>
                                                                    {t.adjuntos.map(adj => <AuditAdjuntoItem key={adj.id} adj={adj} />)}
                                                                </List>
                                                            )}
                                                        </Box>
                                                    </TimelineContent>
                                                </TimelineItem>
                                            );
                                        })}
                                    </Timeline>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* ── Modales ── */}
            <AuditEditModal
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                onSuccess={cargar}
                item={item}
                username={username}
            />
            <AuditTrackingPanel
                open={openTracking}
                onClose={() => setOpenTracking(false)}
                onSuccess={cargar}
                itemId={item.id}
                username={username}
                statusActual={statusActual}
                estados={estados}
                puedeVerTodo={puedeVerTodo}
            />
            <AuditReasignarModal
                open={openReasignar}
                onClose={() => setOpenReasignar(false)}
                onSuccess={cargar}
                itemId={item.id}
                username={username}
                controladorActual={item.controlador_username}
            />
        </Box>
    );
}